import { Button, Group, SimpleGrid, Stack, TagsInput, Text, Textarea, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import HelpPopover from '../HelpPopover/HelpPopover';
import AudienceCard from './AudienceCard';
import ConfidenceBadge from './ConfidenceBadge';

const MAX_AUDIENCES = 3;

const AUDIENCES_HELP = {
  label: 'Audiences',
  what: 'Groups of shoppers the AI writes for. Give each a short name you will recognise later, and say who they are and what they care about.',
  examples: [
    { label: 'Gift buyers: looking for a present within a budget' },
    { label: 'Regulars: know the range and want what is new' },
  ],
};

// A field label with the amber "Please check" next to it while the reader's guess is unconfirmed.
const fieldLabel = (text, field) => (
  <Group gap={6} component="span" wrap="nowrap">
    {text}
    <ConfidenceBadge level={field?.confidenceLevel} />
  </Group>
);

// How the brand sounds: the fields the sales agent and content generation read. Each is stored as
// { value, confidenceLevel }; an edit replaces it with { value }, which also clears "Please check".
const BrandVoiceSection = ({ brandContext, setBrandContext }) => {
  const setField = (name, value) => setBrandContext((prev) => ({ ...prev, [name]: { value } }));

  const audiences = brandContext?.audiences?.value || [];

  const updateAudiences = (change) =>
    setBrandContext((prev) => {
      const list = structuredClone(prev?.audiences?.value || []);
      change(list);
      return { ...prev, audiences: { value: list } };
    });

  return (
    <Stack gap="md">
      <div>
        <Title order={4} fw={600}>
          Brand voice
        </Title>
        <Text size="sm" c="dimmed">
          How your brand sounds. The AI writes your emails, pop-ups and chat this way.
        </Text>
      </div>
      <Textarea
        label={fieldLabel('Archetype', brandContext?.archetypes)}
        aria-label="Archetype"
        autosize
        minRows={1}
        maxRows={4}
        value={brandContext?.archetypes?.value || ''}
        onChange={(e) => setField('archetypes', e.target.value)}
      />
      <Textarea
        label={fieldLabel('Description', brandContext?.description)}
        aria-label="Description"
        autosize
        minRows={2}
        maxRows={6}
        value={brandContext?.description?.value || ''}
        onChange={(e) => setField('description', e.target.value)}
      />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <TagsInput
          label={fieldLabel('Voices', brandContext?.voices)}
          aria-label="Voices"
          data={[]}
          value={brandContext?.voices?.value || []}
          onChange={(value) => setField('voices', value)}
          placeholder="Add voices"
        />
        <TagsInput
          label={fieldLabel('Tones', brandContext?.tones)}
          aria-label="Tones"
          data={[]}
          value={brandContext?.tones?.value || []}
          onChange={(value) => setField('tones', value)}
          placeholder="Add tones"
        />
      </SimpleGrid>
      <div>
        <Group justify="space-between" mb="xs" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={500}>
              Audiences
            </Text>
            <HelpPopover {...AUDIENCES_HELP} />
            <ConfidenceBadge level={brandContext?.audiences?.confidenceLevel} />
          </Group>
          <Text size="xs" c="dimmed">
            {audiences.length} of {MAX_AUDIENCES}
          </Text>
        </Group>
        {audiences.length > 0 && (
          <SimpleGrid cols={{ base: 1, sm: Math.min(audiences.length, 3) }} spacing="sm">
            {audiences.map((audience, index) => (
              <AudienceCard
                // Audiences have no id; the position is the identity while editing.
                key={index}
                index={index}
                audience={audience}
                onChange={(name, value) =>
                  updateAudiences((list) => {
                    list[index][name] = value;
                  })
                }
                onRemove={() => updateAudiences((list) => list.splice(index, 1))}
              />
            ))}
          </SimpleGrid>
        )}
        {audiences.length < MAX_AUDIENCES && (
          <Button
            mt="xs"
            size="xs"
            variant="subtle"
            leftSection={<IconPlus size={14} />}
            onClick={() => updateAudiences((list) => list.push({ title: '', description: '' }))}
          >
            Add audience
          </Button>
        )}
      </div>
    </Stack>
  );
};

export default BrandVoiceSection;
